import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  BugAntIcon,
  LightBulbIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const Support: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'faq' | 'contact' | 'bug'>('faq');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'normal'
  });

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulation d'envoi
    toast.success('Votre message a été envoyé ! Nous vous répondrons sous 24h.');
    setContactForm({
      name: '',
      email: '',
      subject: '',
      message: '',
      priority: 'normal'
    });
  };

  const faqData = [
    {
      category: "Utilisation générale",
      questions: [
        {
          q: "Comment uploader un fichier Excel ?",
          a: "Glissez-déposez votre fichier dans la zone prévue ou cliquez sur 'Sélectionner un fichier'. Formats acceptés : .xlsx, .xls (max 10MB)."
        },
        {
          q: "Pourquoi mes données ont-elles disparu ?",
          a: "Les données sont automatiquement supprimées après 24h pour des raisons de sécurité. Vous devez re-uploader votre fichier pour une nouvelle analyse."
        },
        {
          q: "Comment exporter mes résultats ?",
          a: "Utilisez le bouton 'Export PDF' pour générer un rapport complet ou 'Copier' pour copier les données dans le presse-papiers."
        }
      ]
    },
    {
      category: "Problèmes techniques",
      questions: [
        {
          q: "Erreur 'Fichier non supporté'",
          a: "Vérifiez que votre fichier est au format Excel (.xlsx/.xls) et respecte la structure SOCOA attendue. Consultez la documentation pour plus de détails."
        },
        {
          q: "Les graphiques ne s'affichent pas",
          a: "Vérifiez votre connexion internet et actualisez la page. Si le problème persiste, videz le cache de votre navigateur."
        },
        {
          q: "Données manquantes dans l'analyse",
          a: "Assurez-vous que votre fichier contient toutes les feuilles requises (SUIVI MENSUEL + feuilles hebdomadaires) avec la structure correcte."
        }
      ]
    },
    {
      category: "Fonctionnalités avancées",
      questions: [
        {
          q: "Comment fonctionne l'IA de synthèse ?",
          a: "L'intelligence artificielle analyse vos données et génère automatiquement une synthèse textuelle avec recommandations. Elle peut être configurée avec votre clé OpenAI."
        },
        {
          q: "Puis-je personnaliser les graphiques ?",
          a: "Oui, utilisez les filtres par métrique (CA, Fréquentation, PM) et par année. D'autres options de personnalisation sont en développement."
        },
        {
          q: "Comment interpréter le score de performance ?",
          a: "Le score combine plusieurs facteurs : atteinte des objectifs, croissance vs N-1, et consistance mensuelle. Plus le score est élevé, meilleures sont les performances."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* En-tête */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              🆘 Support & Aide
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trouvez rapidement des réponses à vos questions ou contactez notre équipe
            </p>
          </div>

          {/* Statistiques support */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <ClockIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-800">&lt; 2h</div>
              <div className="text-sm text-gray-600">Temps de réponse moyen</div>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-800">98%</div>
              <div className="text-sm text-gray-600">Problèmes résolus</div>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <UserGroupIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-800">24/7</div>
              <div className="text-sm text-gray-600">Support disponible</div>
            </motion.div>
            
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <LightBulbIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-800">150+</div>
              <div className="text-sm text-gray-600">Questions résolues</div>
            </motion.div>
          </div>

          {/* Navigation des onglets */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg shadow-md p-1 flex">
              <button
                onClick={() => setActiveTab('faq')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'faq' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <QuestionMarkCircleIcon className="h-5 w-5 inline mr-2" />
                FAQ
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'contact' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5 inline mr-2" />
                Contact
              </button>
              <button
                onClick={() => setActiveTab('bug')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === 'bug' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BugAntIcon className="h-5 w-5 inline mr-2" />
                Signaler un bug
              </button>
            </div>
          </div>

          {/* Contenu des onglets */}
          {activeTab === 'faq' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              {faqData.map((category, categoryIndex) => (
                <div key={categoryIndex} className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                    <QuestionMarkCircleIcon className="h-6 w-6 text-blue-600 mr-2" />
                    {category.category}
                  </h2>
                  <div className="space-y-4">
                    {category.questions.map((item, index) => (
                      <div key={index} className="border-l-4 border-blue-200 bg-blue-50 p-4">
                        <h3 className="font-semibold text-blue-800 mb-2">
                          Q: {item.q}
                        </h3>
                        <p className="text-blue-700">
                          R: {item.a}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Section d'aide rapide */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                  🚀 Aide rapide
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Vérification rapide</h3>
                    <p className="text-sm text-gray-600">
                      Assurez-vous que votre fichier respecte le format standard
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <ExclamationTriangleIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Problème persistant ?</h3>
                    <p className="text-sm text-gray-600">
                      Contactez-nous avec votre fichier pour un diagnostic personnalisé
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                      <LightBulbIcon className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Suggestion</h3>
                    <p className="text-sm text-gray-600">
                      Consultez la documentation pour des guides détaillés
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'contact' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 gap-8"
            >
              {/* Formulaire de contact */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                  <EnvelopeIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Nous contacter
                </h2>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sujet *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Résumé de votre demande"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priorité
                    </label>
                    <select
                      value={contactForm.priority}
                      onChange={(e) => setContactForm({...contactForm, priority: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="low">Basse - Question générale</option>
                      <option value="normal">Normale - Problème technique</option>
                      <option value="high">Haute - Urgence business</option>
                      <option value="critical">Critique - Système inaccessible</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Décrivez votre problème ou question en détail..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Envoyer le message
                  </button>
                </form>
              </div>

              {/* Informations de contact */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                    📞 Autres moyens de contact
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <div>
                        <div className="font-medium">Email</div>
                        <div className="text-gray-600">
                          <a href="mailto:support@bcm-informatique.com" className="text-blue-600 hover:text-blue-800 transition-colors">
                            support@bcm-informatique.com
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <div>
                        <div className="font-medium">Téléphone</div>
                        <div className="text-gray-600">
                          <a href="tel:+33559639423" className="text-blue-600 hover:text-blue-800 transition-colors">
                            05 59 63 94 23
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <div>
                        <div className="font-medium">Site web</div>
                        <div className="text-gray-600">
                          <a href="https://bcm-informatique.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 transition-colors">
                            bcm-informatique.com
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <div>
                        <div className="font-medium">Horaires</div>
                        <div className="text-gray-600">Lun-Ven: 9h-18h</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">
                    💡 Avant de nous contacter
                  </h3>
                  <ul className="text-blue-700 text-sm space-y-2">
                    <li>• Consultez d'abord la FAQ ci-dessus</li>
                    <li>• Vérifiez que votre fichier respecte le format requis</li>
                    <li>• Notez les messages d'erreur exacts</li>
                    <li>• Préparez votre fichier Excel pour diagnostic</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-3">
                    🚀 Temps de réponse
                  </h3>
                  <div className="text-green-700 text-sm space-y-1">
                    <div>• <strong>Critique:</strong> &lt; 1h</div>
                    <div>• <strong>Haute:</strong> &lt; 4h</div>
                    <div>• <strong>Normale:</strong> &lt; 24h</div>
                    <div>• <strong>Basse:</strong> &lt; 48h</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'bug' && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <BugAntIcon className="h-6 w-6 text-red-600 mr-2" />
                Signaler un bug
              </h2>
              
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-red-800 font-medium">Informations importantes</p>
                    <p className="text-red-700 text-sm mt-1">
                      Pour nous aider à résoudre rapidement le problème, veuillez fournir un maximum de détails.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de problème *
                    </label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                      <option>Erreur d'upload</option>
                      <option>Graphiques incorrects</option>
                      <option>Calculs erronés</option>
                      <option>Export PDF défaillant</option>
                      <option>Interface utilisateur</option>
                      <option>Performance lente</option>
                      <option>Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sévérité *
                    </label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
                      <option>Mineure - Gêne légère</option>
                      <option>Modérée - Fonctionnalité affectée</option>
                      <option>Majeure - Blocage partiel</option>
                      <option>Critique - Application inutilisable</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description du problème *
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Décrivez précisément le problème rencontré..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Étapes pour reproduire le bug *
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="1. J'ai uploadé un fichier Excel&#10;2. J'ai cliqué sur...&#10;3. L'erreur suivante est apparue..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message d'erreur (si applicable)
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Copiez-collez le message d'erreur exact..."
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Navigateur utilisé
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="ex: Chrome 120, Firefox 115..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Système d'exploitation
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="ex: Windows 11, macOS 14..."
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-800 mb-2">📎 Fichiers joints</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Si possible, joignez votre fichier Excel pour nous aider à reproduire le problème.
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Signaler le bug
                </button>
              </form>
            </motion.div>
          )}

        </motion.div>
      </div>
    </div>
  );
};

export default Support; 